import type {
  GraphEntityTypeV2,
  GraphNodeV2,
  TransactionGraphResponse
} from "@salesko/contracts";
import { Loader2, Minus, Network, Plus, X } from "lucide-react";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import type { Dictionary } from "../../i18n";
import { useI18n } from "../../i18n";
import { formatMinorUnitAmount, transactionStatusLabel } from "../lib/activityFeed";
import { compactDateTime } from "../lib/format";

const GraphCanvas = lazy(() =>
  import("../../components/GraphCanvas").then((module) => ({ default: module.GraphCanvas }))
);

const entityOrder: GraphEntityTypeV2[] = [
  "person",
  "organisation",
  "event",
  "opportunity",
  "transaction"
];

const entityColors: Record<GraphEntityTypeV2, string> = {
  person: "#4a8cb0",
  organisation: "#6ba872",
  event: "#d9974f",
  opportunity: "#cba94f",
  note: "#837a6a",
  transaction: "#7d5ac7"
};

type TransactionGraphDialogProps = {
  error?: string | undefined;
  onClose: () => void;
  pending: boolean;
  response: TransactionGraphResponse | null;
  transactionId: string | null;
};

export function TransactionGraphDialog({
  error,
  onClose,
  pending,
  response,
  transactionId
}: TransactionGraphDialogProps) {
  const { locale, t } = useI18n();
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLElement | null>(null);
  const frame = response?.canonicalTransactionId === transactionId ? response.frameRecord.frame : null;
  const [filters, setFilters] = useState<Record<GraphEntityTypeV2, boolean>>(() => enabledFilters());
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(transactionId);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  useEffect(() => {
    if (!transactionId) return;
    setFilters(enabledFilters());
    setSelectedNodeId(transactionId);
    setSelectedEdgeId(null);
    setZoomLevel(1);
    closeRef.current?.focus();
  }, [transactionId, response?.frameRecord.id]);

  useEffect(() => {
    if (!transactionId) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key === "Tab" && dialogRef.current) {
        const focusable = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        );
        const first = focusable[0];
        const last = focusable.at(-1);
        if (!first || !last) return;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, transactionId]);

  const counts = useMemo(() => transactionGraphEntityCounts(frame?.graph.nodes ?? []), [frame]);
  const enabledEntityTypes = useMemo(
    () => entityOrder.filter((entityType) => filters[entityType] !== false),
    [filters]
  );
  const selectedNode = frame?.graph.nodes.find((node) => node.id === selectedNodeId) ?? null;

  if (!transactionId) return null;

  return (
    <div
      className="transaction-graph-backdrop"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
    >
      <section
        aria-describedby="transaction-graph-read-only"
        aria-labelledby="transaction-graph-title"
        aria-modal="true"
        className="transaction-graph-dialog"
        ref={dialogRef}
        role="dialog"
      >
        <header className="transaction-graph-header">
          <div>
            <p className="eyebrow">{t.activityFeed.subjectTypes.transaction}</p>
            <h2 id="transaction-graph-title">{t.activityFeed.transactionGraphTitle}</h2>
            <span id="transaction-graph-read-only">{t.activityFeed.transactionGraphReadOnly}</span>
          </div>
          <button
            aria-label={t.activityFeed.closeTransactionGraph}
            className="icon-button"
            onClick={onClose}
            ref={closeRef}
            title={t.activityFeed.closeTransactionGraph}
            type="button"
          >
            <X aria-hidden="true" size={18} />
          </button>
        </header>

        {error ? (
          <p className="transaction-graph-state is-error" role="alert">{t.activityFeed.transactionGraphUnavailable}</p>
        ) : pending || !frame ? (
          <div aria-live="polite" className="transaction-graph-state" role="status">
            <Loader2 aria-hidden="true" className="spin" size={18} />
            <span>{t.activityFeed.loadingTransactionGraph}</span>
          </div>
        ) : (
          <div className="transaction-graph-layout">
            <aside className="transaction-graph-legend" aria-label={t.leftRail.entitiesLabel}>
              <div className="section-label">
                <span>{t.leftRail.entitiesLabel}</span>
                <Network aria-hidden="true" size={15} />
              </div>
              {entityOrder.map((entityType) => (
                <button
                  aria-pressed={filters[entityType] !== false}
                  className={filters[entityType] !== false ? "transaction-entity-filter is-active" : "transaction-entity-filter"}
                  key={entityType}
                  onClick={() => setFilters((current) => ({ ...current, [entityType]: !current[entityType] }))}
                  type="button"
                >
                  <span aria-hidden="true" className="transaction-entity-dot" style={{ background: entityColors[entityType] }} />
                  <span>{transactionGraphEntityLabel(entityType, t)}</span>
                  <strong>{counts[entityType]}</strong>
                </button>
              ))}
            </aside>

            <div className="transaction-graph-canvas">
              <Suspense
                fallback={<div className="transaction-graph-state">{t.graphStage.loadingGraphField}</div>}
              >
                <GraphCanvas
                  enabledEntityTypes={enabledEntityTypes}
                  focusNodeId={selectedNodeId}
                  frame={frame}
                  matchNodeIds={[]}
                  onSelectEdge={setSelectedEdgeId}
                  onSelectNode={(nodeId) => {
                    setSelectedNodeId(nodeId);
                    setSelectedEdgeId(null);
                  }}
                  onZoomChange={setZoomLevel}
                  selectedEdgeId={selectedEdgeId}
                  selectedNodeId={selectedNodeId}
                  spotlightNodeIds={selectedNodeId ? [selectedNodeId] : []}
                  variant="dialog"
                  viewMode="flat"
                  zoomLevel={zoomLevel}
                />
              </Suspense>
              <div className="transaction-graph-zoom" aria-label={t.graphStage.graphControlsAriaLabel}>
                <button
                  aria-label={t.graphStage.zoomIn}
                  className="icon-button"
                  onClick={() => setZoomLevel((current) => clampZoom(current + 0.16))}
                  type="button"
                >
                  <Plus aria-hidden="true" size={17} />
                </button>
                <button
                  aria-label={t.graphStage.zoomOut}
                  className="icon-button"
                  onClick={() => setZoomLevel((current) => clampZoom(current - 0.16))}
                  type="button"
                >
                  <Minus aria-hidden="true" size={17} />
                </button>
              </div>
            </div>

            <aside className="transaction-graph-node-detail" aria-live="polite">
              {selectedNode ? (
                <>
                  <span className="eyebrow">{transactionGraphEntityLabel(selectedNode.type, t)}</span>
                  <h3>{selectedNode.label}</h3>
                  {selectedNode.summary ? <p>{selectedNode.summary}</p> : null}
                  {selectedNode.type === "transaction" ? (
                    <dl>
                      <div>
                        <dt>{t.activityFeed.status}</dt>
                        <dd>{transactionStatusLabel(selectedNode.data.status, t.activityFeed)}</dd>
                      </div>
                      <div>
                        <dt>{t.activityFeed.amount}</dt>
                        <dd>{formatMinorUnitAmount(selectedNode.data.amountMinor, selectedNode.data.currency, locale)}</dd>
                      </div>
                      <div>
                        <dt>{t.activityFeed.occurredAt}</dt>
                        <dd><time dateTime={selectedNode.occurredAt}>{compactDateTime(selectedNode.occurredAt)}</time></dd>
                      </div>
                    </dl>
                  ) : null}
                  <p className="transaction-graph-evidence"><strong>{t.activityFeed.evidenceText}</strong> {selectedNode.evidence}</p>
                </>
              ) : (
                <p>{t.activityFeed.selectTransactionGraphNode}</p>
              )}
            </aside>
          </div>
        )}
      </section>
    </div>
  );
}

export function transactionGraphEntityCounts(nodes: GraphNodeV2[]): Record<GraphEntityTypeV2, number> {
  const counts: Record<GraphEntityTypeV2, number> = {
    person: 0,
    organisation: 0,
    event: 0,
    opportunity: 0,
    note: 0,
    transaction: 0
  };
  for (const node of nodes) counts[node.type] += 1;
  return counts;
}

function transactionGraphEntityLabel(entityType: GraphEntityTypeV2, t: Dictionary): string {
  switch (entityType) {
    case "person": return t.entities.person;
    case "organisation": return t.entities.organisation;
    case "event": return t.entities.event;
    case "opportunity": return t.entities.opportunity;
    case "note": return t.entities.note;
    case "transaction": return t.activityFeed.subjectTypes.transaction;
  }
}

function enabledFilters(): Record<GraphEntityTypeV2, boolean> {
  return {
    person: true,
    organisation: true,
    event: true,
    opportunity: true,
    note: true,
    transaction: true
  };
}

function clampZoom(value: number): number {
  return Math.max(0.65, Math.min(1.8, value));
}
